import { default as ClassNames, default as classNames } from "classnames";
import { EmblaCarouselType } from "embla-carousel";
import { FunctionComponent, useState } from "react";
import buttonStyles from "../stylesheets/components/button.module.css";
import styles from "../stylesheets/components/highlights-layout-6.module.css";
import moduleBaseStyles from "../stylesheets/components/module-base.module.css";
import {
  AspectRatio,
  ColorScheme,
  ContainerQueries,
  HighlightsDisplayType,
  HighlightsItem,
  Language,
  MediaAlign,
  RichEditorState,
  TitleAndSubtitleTextAlign,
} from "../types/index.js";
import { css } from "../utils/emotion.js";
import CroppedImage from "./CroppedImage.js";
import HighlightHeading from "./HighlightHeading.js";
import ModuleHeadings from "./ModuleHeadings.js";
import ReadMoreCollapse from "./ReadMoreCollapse.js";
import Slider from "./Slider.js";
import SliderArrow from "./SliderArrow.js";

interface Props {
  title: string;
  subtitle: string;
  textAlign: TitleAndSubtitleTextAlign;
  isFirstOnPage: boolean;
  scheme: ColorScheme;
  items: HighlightsItem[];
  collapsedLinesCount: number | undefined;
  languageId: Language;
  richEditor: RichEditorState;
  isPreview: boolean;
  displayType: HighlightsDisplayType;
  activeModuleId: string | undefined;
  transparentSliderArrowBackground: boolean;
  mediaAlign: MediaAlign;
  maxColumnsCount: number;
  queries: ContainerQueries;
}

const HighlightsLayout6: FunctionComponent<Props> = ({
  title,
  subtitle,
  textAlign,
  isFirstOnPage,
  scheme,
  items,
  collapsedLinesCount,
  languageId,
  richEditor,
  isPreview,
  displayType,
  activeModuleId,
  transparentSliderArrowBackground,
  mediaAlign,
  maxColumnsCount,
  queries,
}) => {
  const titleBackground = scheme.main.background;
  const [slider, setSlider] = useState<EmblaCarouselType>();
  const fixedAspectRatio: AspectRatio = 1.5;
  const hasHighlightsTitle = !!title;

  const highlights = items.map(
    (
      { id, title, subtitle, picture, imageDetail, buttons, text, textAlign },
      index,
    ) => (
      <article
        key={id}
        className={ClassNames(styles.HighlightsLayout6__Item, {
          [styles["HighlightsLayout6__Item--list"]]: displayType === "list",
        })}
      >
        <div className={styles.HighlightsLayout6__ItemPicture}>
          <CroppedImage
            aspectRatio={queries["Query--large"] ? undefined : fixedAspectRatio}
            picture={picture}
            width={1280}
            detail={imageDetail}
            sizes="(min-width: 640px) 50vw, (min-width: 1024px) 33vw, (min-width: 1600px) 500px, 100vw"
          />
        </div>

        <div className={styles.HighlightsLayout6__ItemContent}>
          <div className={styles.HighlightsLayout6__ItemContentText}>
            <HighlightHeading
              title={{
                value: title,
                className: ClassNames(
                  styles.HighlightsLayout6__ItemTitle,
                  css({ color: scheme.main.title }),
                ),
                children: (
                  <span
                    className={ClassNames(
                      styles.HighlightsLayout6__ItemTitleInner,
                      css({
                        backgroundColor: titleBackground,
                        boxShadow:
                          `7px 3px 0 ${titleBackground}, -7px 3px 0 ${titleBackground}, ` +
                          `7px 0 0 ${titleBackground}, -7px 0 0 ${titleBackground}`,
                      }),
                    )}
                  >
                    {title}
                  </span>
                ),
              }}
              subtitle={{
                value: subtitle,
                className: styles.HighlightsLayout6__ItemSubtitle,
              }}
              highlightIndex={index}
              hasHighlightsTitle={hasHighlightsTitle}
              isFirstOnPage={isFirstOnPage}
            />

            {text && (
              <div className={styles.HighlightsLayout6__Description}>
                {collapsedLinesCount !== undefined ? (
                  <ReadMoreCollapse
                    backgroundColor={scheme.main.separator}
                    languageId={languageId}
                    collapsedLinesCount={collapsedLinesCount}
                    isEditing={!!richEditor[id]}
                  >
                    {text}
                  </ReadMoreCollapse>
                ) : (
                  text
                )}
              </div>
            )}
          </div>
          {!!buttons.length && (
            <div
              className={classNames(
                styles.HighlightsLayout6__Buttons,
                textAlign.buttons !== "justify"
                  ? buttonStyles[`ButtonGroup--align-${textAlign.buttons}`]
                  : undefined,
              )}
            >
              {buttons}
            </div>
          )}
        </div>
      </article>
    ),
  );

  return (
    <div
      className={ClassNames(
        styles.HighlightsLayout6,
        styles[`HighlightsLayout6--media-${mediaAlign}`],
        css({ background: scheme.main.separator, color: scheme.main.text }),
        {
          "TextAlign--right": mediaAlign === "right",
          [styles["HighlightsLayout6--columns-2"]]: maxColumnsCount === 2,
        },
      )}
    >
      <div
        className={ClassNames(
          moduleBaseStyles.Module__Wrapper,
          moduleBaseStyles["Module__Wrapper--default-padding"],
        )}
      >
        <ModuleHeadings
          scheme={scheme}
          isFirstOnPage={isFirstOnPage}
          textAlign={textAlign}
          title={title}
          subtitle={subtitle}
        />

        <div className={styles.HighlightsLayout6__Items}>
          {displayType === "slider" && (
            <Slider
              slideClassName={styles.HighlightsLayout6__Slide}
              isPreview={isPreview}
              sliderEffect="slide"
              onSliderLoaded={setSlider}
              slidesToShow={queries["Query--xlarge"] ? maxColumnsCount : 1}
              activePreviewSlideId={activeModuleId}
              slideIds={items.map(({ id }) => id)}
            >
              {highlights}
            </Slider>
          )}
          {displayType === "list" && (
            <div className={styles.HighlightsLayout6__List}>{highlights}</div>
          )}
          {displayType === "slider" && items.length > 1 && (
            <div>
              <SliderArrow
                className={ClassNames(
                  styles.HighlightsLayout6__SliderArrow,
                  styles["HighlightsLayout6__SliderArrow--left"],
                )}
                iconClassName={styles.HighlightsLayout6__SliderArrowIcon}
                direction="left"
                onClick={() => slider?.scrollPrev()}
                scheme={scheme}
                transparentBackground={transparentSliderArrowBackground}
                languageId={languageId}
              />
              <SliderArrow
                className={ClassNames(
                  styles.HighlightsLayout6__SliderArrow,
                  styles["HighlightsLayout6__SliderArrow--right"],
                )}
                iconClassName={styles.HighlightsLayout6__SliderArrowIcon}
                direction="right"
                onClick={() => slider?.scrollNext()}
                scheme={scheme}
                transparentBackground={transparentSliderArrowBackground}
                languageId={languageId}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HighlightsLayout6;
